import { FunctionComponent, useMemo } from "react";

import {
  Dialog,
  Stack,
  SubmissionForm,
  Text,
  TextInputField
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { TenantNetworkAuthenticationSettingsDto } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { ValidationMessages } from "@libs/validation/validation-messages.enum";
import { IP_REGEX } from "@libs/validation/validation-rules.constants";

const PLACEHOLDER = "x.x.x.x";
export interface IpRangeFormDialogProps {
  networkSetting?: TenantNetworkAuthenticationSettingsDto | null;
  onDismiss: () => void;
  onSubmit: (formData: AddIpRangeFormValues, id?: string) => Promise<void>;
}

export interface AddIpRangeFormValues
  extends Pick<
    TenantNetworkAuthenticationSettingsDto,
    "startAddress" | "endAddress" | "description"
  > {}

// Convert IPv4 address to a 32-bit number for comparison
const ipToNumber = (ip: string): number =>
  ip
    .split(".")
    .map(octet => parseInt(octet, 10))
    .reduce((acc, octet) => (acc << 8) + octet, 0) >>> 0;

const ipRangeValidator = new Validator<AddIpRangeFormValues>(validator => ({
  startAddress: [
    validator.string().required(),
    validator
      .string()
      .pattern(IP_REGEX, { message: ValidationMessages.ipRange }),
    validator.custom().func<"startAddress">((value, parentValues) => {
      // Ensure the start address is less than or equal to the end address.
      return ipToNumber(value) > ipToNumber(parentValues.endAddress)
        ? ValidationMessages.ipStartLessThenOrEqualToEnd
        : value;
    })
  ],
  endAddress: [
    validator.string().required(),
    validator
      .string()
      .pattern(IP_REGEX, { message: ValidationMessages.ipRange })
  ],
  description: [validator.string().maxLength(200)]
}));

export const NetworkAuthenticationSettingsFormDialog: FunctionComponent<IpRangeFormDialogProps> = ({
  onDismiss,
  networkSetting,
  onSubmit
}) => {
  const defaultValues: AddIpRangeFormValues = useMemo(() => {
    return {
      startAddress: networkSetting?.startAddress ?? "",
      endAddress: networkSetting?.endAddress ?? "",
      description: networkSetting?.description ?? ""
    };
  }, [networkSetting]);

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: networkSetting ? "Edit IpRange" : "Add IpRange",
        showCloseButton: true,
        onDismiss: () => onDismiss()
      }}
      minWidth={450}
    >
      <SubmissionForm<AddIpRangeFormValues>
        onSubmit={values => onSubmit(values, networkSetting?.id)}
        initialValues={defaultValues}
        cancelButtonProps={{ onClick: onDismiss }}
        validate={ipRangeValidator.validate}
      >
        {() => (
          <>
            <Stack tokens={{ childrenGap: 8 }} verticalAlign="start" horizontal>
              <TextInputField
                label="IP Range Begin"
                name="startAddress"
                placeholder={PLACEHOLDER}
              />
              <Text styles={{ root: { paddingTop: 32 } }}>to</Text>
              <TextInputField
                name="endAddress"
                label="IP Range End"
                placeholder={PLACEHOLDER}
              />
            </Stack>
            <Stack>
              <TextInputField name="description" label="Description " />
            </Stack>
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
